import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, HelpCircle, ChevronDown, ChevronRight, Globe, Factory, TrendingUp, Newspaper, Package, Bell, Bookmark, BarChart3, Shield, Users, ArrowLeftRight, Lightbulb, FileText, Menu, X, User, LogOut, Settings, Anchor, Lock } from 'lucide-react';
import { useUserProfile } from '@/lib/useUserProfile';
import { useAuth } from '@/lib/AuthContext';
import { HelpPanel } from '@/components/HelpPanel';
import { NotificationBell } from '@/components/NotificationBell';

// ─── Route → active section mapping ───
const SECTION_MAP = {
  '/dashboard': 'dashboard',
  '/countries': 'intelligence', '/sectors': 'intelligence', '/history': 'intelligence', '/signals': 'intelligence',
  '/suppliers': 'supply-chain', '/alerts': 'supply-chain', '/watchlist': 'supply-chain',
  '/concentration': 'analytics', '/aukus': 'analytics', '/modern-slavery': 'analytics', '/sourcing': 'analytics', '/ports-routes': 'analytics',
  '/sourcing-alternatives': 'sourcing',
  '/scenarios': 'scenarios',
  '/reports': 'reports',
};

const getActiveSection = (pathname) => {
  // Exact match first
  if (SECTION_MAP[pathname]) return SECTION_MAP[pathname];
  // Prefix match for detail pages
  if (pathname.startsWith('/countries/')) return 'intelligence';
  if (pathname.startsWith('/sectors/')) return 'intelligence';
  if (pathname.startsWith('/suppliers/')) return 'supply-chain';
  if (pathname.startsWith('/reports/')) return 'reports';
  if (pathname.startsWith('/briefings/')) return 'intelligence';
  return 'dashboard';
};

// ─── Dropdown definitions ───
const INTELLIGENCE_ITEMS = [
  { icon: Globe, label: 'Countries', desc: 'Global country risk profiles', href: '/countries', badge: '42 countries' },
  { icon: Factory, label: 'Sectors', desc: 'Industry sector risk assessment', href: '/sectors', badge: '6 sectors' },
  { icon: TrendingUp, label: 'Trends', desc: 'Historical risk trends', href: '/history', badge: '3M / 6M / 12M' },
  { icon: Newspaper, label: 'News & Signals', desc: 'Latest risk signals and news', href: '/signals', badge: 'Live feed' },
];

const SUPPLY_CHAIN_ITEMS = [
  { icon: Package, label: 'Suppliers', desc: 'Your supplier portfolio and risk', href: '/suppliers', badgeKey: 'suppliers' },
  { icon: Bell, label: 'Alerts', desc: 'Alert rules and notification preferences', href: '/alerts', badgeKey: 'alerts' },
  { icon: Bookmark, label: 'Watchlist', desc: 'Countries and suppliers you\'re tracking', href: '/watchlist', badge: 'Quick access' },
];

const ANALYTICS_ITEMS = [
  { icon: Anchor, label: 'Ports & Routes', desc: 'Port intelligence and trade route conditions', href: '/ports-routes', badge: '50+ ports' },
  { icon: BarChart3, label: 'Concentration', desc: 'Supplier portfolio concentration analysis', href: '/concentration' },
  { icon: Shield, label: 'AUKUS Compliance', desc: 'Sovereignty and allied-nation compliance', href: '/aukus' },
  { icon: Users, label: 'Modern Slavery', desc: 'Labour risk and ILO indicator assessment', href: '/modern-slavery' },
];

// ─── Breadcrumb definitions ───
const BREADCRUMBS = {
  '/countries': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Countries' }],
  '/sectors': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Sectors' }],
  '/suppliers': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Suppliers' }],
  '/alerts': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Alerts' }],
  '/watchlist': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Watchlist' }],
  '/history': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Trends' }],
  '/signals': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'News & Signals' }],
  '/concentration': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Analytics', href: '/ports-routes' }, { label: 'Concentration' }],
  '/aukus': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Analytics', href: '/ports-routes' }, { label: 'AUKUS Compliance' }],
  '/modern-slavery': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Analytics', href: '/ports-routes' }, { label: 'Modern Slavery' }],
  '/sourcing': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Analytics', href: '/ports-routes' }, { label: 'Sourcing' }],
  '/ports-routes': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Analytics' }, { label: 'Ports & Routes' }],
  '/scenarios': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Scenarios' }],
  '/sourcing-alternatives': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Sourcing alternatives' }],
  '/reports': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Reports' }],
  '/settings': [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Settings' }],
};

const getBreadcrumbs = (pathname, extra) => {
  if (BREADCRUMBS[pathname]) return BREADCRUMBS[pathname];
  if (pathname.startsWith('/countries/') && extra) return [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Countries', href: '/countries' }, { label: extra }];
  if (pathname.startsWith('/sectors/') && extra) return [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Sectors', href: '/sectors' }, { label: extra }];
  if (pathname.startsWith('/suppliers/') && extra) return [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Suppliers', href: '/suppliers' }, { label: extra }];
  if (pathname.startsWith('/reports/') && extra) return [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Reports', href: '/reports' }, { label: extra }];
  if (pathname.startsWith('/briefings/') && extra) return [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Intelligence', href: '/countries' }, { label: extra }];
  return null;
};

// ─── Search data ───
const SEARCH_INDEX = [
  { cat: 'Countries', items: [
    { name: 'China', href: '/countries/CN' }, { name: 'Vietnam', href: '/countries/VN' }, { name: 'Indonesia', href: '/countries/ID' },
    { name: 'Thailand', href: '/countries/TH' }, { name: 'Malaysia', href: '/countries/MY' }, { name: 'Philippines', href: '/countries/PH' },
    { name: 'India', href: '/countries/IN' }, { name: 'Japan', href: '/countries/JP' }, { name: 'South Korea', href: '/countries/KR' },
    { name: 'Taiwan', href: '/countries/TW' }, { name: 'Singapore', href: '/countries/SG' }, { name: 'Bangladesh', href: '/countries/BD' },
    { name: 'Cambodia', href: '/countries/KH' }, { name: 'Myanmar', href: '/countries/MM' }, { name: 'New Zealand', href: '/countries/NZ' },
    { name: 'Papua New Guinea', href: '/countries/PG' }, { name: 'Laos', href: '/countries/LA' },
  ]},
  { cat: 'Analytics', items: [
    { name: 'Ports & Routes', href: '/ports-routes' }, { name: 'Concentration Analysis', href: '/concentration' }, { name: 'AUKUS Compliance', href: '/aukus' },
    { name: 'Modern Slavery Risk', href: '/modern-slavery' }, { name: 'Comparative Sourcing', href: '/sourcing' },
  ]},
  { cat: 'Pages', items: [
    { name: 'Dashboard', href: '/dashboard' }, { name: 'Suppliers', href: '/suppliers' }, { name: 'Alerts', href: '/alerts' },
    { name: 'Reports', href: '/reports' }, { name: 'Scenarios', href: '/scenarios' }, { name: 'Trends', href: '/history' },
    { name: 'News & Signals', href: '/signals' }, { name: 'Watchlist', href: '/watchlist' },
    { name: 'Countries', href: '/countries' }, { name: 'Sectors', href: '/sectors' },
    { name: 'Sourcing Alternatives', href: '/sourcing-alternatives' },
  ]},
];


// ════════════════════════════════════════════════
// NavBar Component
// ════════════════════════════════════════════════

export const NavBar = ({ breadcrumbExtra }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, clearProfile } = useUserProfile();
  const { user: authUser, logout } = useAuth();
  const companyName = profile?.company_name || 'Demo Company';
  const activeSection = getActiveSection(location.pathname);
  const crumbs = getBreadcrumbs(location.pathname, breadcrumbExtra);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const hoverTimeout = useRef(null);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdown(null);
      if (!e.target.closest('[data-account-dropdown]')) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cmd+K search, ? help
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && !e.target.isContentEditable) {
        setHelpOpen(prev => !prev);
      }
      if (e.key === 'Escape') { setSearchOpen(false); setOpenDropdown(null); setHelpOpen(false); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close mobile on navigate
  useEffect(() => { setMobileOpen(false); setOpenDropdown(null); }, [location.pathname]);

  const handleHoverEnter = useCallback((key) => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setOpenDropdown(key), 150);
  }, []);

  const handleHoverLeave = useCallback(() => {
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setOpenDropdown(null), 200);
  }, []);

  const toggleDropdown = useCallback((key) => {
    setOpenDropdown(prev => prev === key ? null : key);
  }, []);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D3B4F] border-b border-white/10" data-testid="main-nav">
        <div className="px-4 lg:px-6 py-0 flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-white font-bold text-lg">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold">TradePath</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
            <NavItem label="Dashboard" href="/dashboard" active={activeSection === 'dashboard'} navigate={navigate} />
            <DropdownNavItem label="Intelligence" sectionKey="intelligence" active={activeSection === 'intelligence'}
              items={INTELLIGENCE_ITEMS} openDropdown={openDropdown} onHoverEnter={handleHoverEnter}
              onHoverLeave={handleHoverLeave} onToggle={toggleDropdown} navigate={navigate} />
            <DropdownNavItem label="My Supply Chain" sectionKey="supply-chain" active={activeSection === 'supply-chain'}
              items={SUPPLY_CHAIN_ITEMS} openDropdown={openDropdown} onHoverEnter={handleHoverEnter}
              onHoverLeave={handleHoverLeave} onToggle={toggleDropdown} navigate={navigate} />
            <DropdownNavItem label="Analytics" sectionKey="analytics" active={activeSection === 'analytics'}
              items={ANALYTICS_ITEMS} openDropdown={openDropdown} onHoverEnter={handleHoverEnter}
              onHoverLeave={handleHoverLeave} onToggle={toggleDropdown} navigate={navigate} />
            <NavItem label="Scenarios" href="/scenarios" active={activeSection === 'scenarios'} navigate={navigate} />
            <NavItem label="Sourcing" href="/sourcing-alternatives" active={activeSection === 'sourcing'} navigate={navigate} />
            <NavItem label="Reports" href="/reports" active={activeSection === 'reports'} navigate={navigate} />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <button onClick={() => setSearchOpen(true)} className="text-[#B0BEC5] hover:text-[#4DD0E1] transition-colors duration-200 p-2" data-testid="nav-search-btn" title="Search (Ctrl+K)">
              <Search className="h-4 w-4" />
            </button>
            <NotificationBell />
            <button onClick={() => setHelpOpen(true)} className="text-[#B0BEC5] hover:text-[#4DD0E1] transition-colors duration-200 p-2" data-testid="nav-help-btn" title="Help (?)" aria-label="Open help panel">
              <HelpCircle className="h-4 w-4" />
            </button>
            {/* Account dropdown */}
            <div className="relative hidden lg:block" data-account-dropdown>
              {authUser ? (
                <>
                  <button onClick={() => setAccountOpen(!accountOpen)}
                    className="flex items-center gap-1.5 text-[#B0BEC5] hover:text-white transition-colors duration-200 text-sm px-2 py-1.5 rounded" data-testid="nav-account-btn">
                    <span className="max-w-[140px] truncate">{authUser.name || companyName}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  {accountOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)] py-1 z-50">
                      <div className="px-4 py-2.5 border-b border-[#E0E0E0]">
                        <p className="text-sm font-semibold text-[#0A2533]">{authUser.name || companyName}</p>
                        <p className="text-xs text-[#4E6974]">{authUser.email}</p>
                      </div>
                      <button onClick={() => { navigate('/settings'); setAccountOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#4E6974] hover:bg-[#F5F5F5] hover:text-[#0A2533] transition-colors" data-testid="nav-account-settings">
                        <Settings className="h-4 w-4" /> Dashboard preferences
                      </button>
                      {authUser.is_platform_admin && (
                        <button onClick={() => { navigate('/admin'); setAccountOpen(false); }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#0097A7] hover:bg-[#F5F5F5] font-medium transition-colors" data-testid="nav-admin-console-link">
                          <Lock className="h-4 w-4" /> Admin console
                        </button>
                      )}
                      <button onClick={async () => { await logout(); clearProfile(); navigate('/signin'); setAccountOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#4E6974] hover:bg-[#F5F5F5] hover:text-[#0A2533] transition-colors" data-testid="nav-account-signout">
                        <LogOut className="h-4 w-4" /> Sign out
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <button onClick={() => navigate('/signin')}
                  className="flex items-center gap-1.5 text-[#B0BEC5] hover:text-white transition-colors duration-200 text-sm px-3 py-1.5 border border-white/20 rounded-lg hover:border-white/40" data-testid="nav-signin-btn">
                  <User className="h-3.5 w-3.5" /> Sign in
                </button>
              )}
            </div>
            {/* Mobile hamburger */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white p-2" data-testid="nav-mobile-toggle">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && <MobileMenu navigate={navigate} activeSection={activeSection} companyName={companyName} clearProfile={clearProfile} onClose={() => setMobileOpen(false)} />}

      {/* Breadcrumbs */}
      {crumbs && location.pathname !== '/dashboard' && (
        <div className="fixed top-14 left-0 right-0 z-40 bg-white border-b border-[#E0E0E0]" data-testid="breadcrumbs">
          <div className="px-4 lg:px-6 py-2 max-w-[1600px] mx-auto flex items-center gap-1.5 text-xs text-[#4E6974]">
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3 w-3 text-[#B0BEC5]" />}
                {c.href ? <Link to={c.href} className="hover:text-[#0097A7] transition-colors">{c.label}</Link> : <span className="text-[#0D3B4F] font-medium">{c.label}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search modal */}
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} navigate={navigate} />}

      {/* Global help panel */}
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};


// ─── Nav item (no dropdown) ───
const NavItem = ({ label, href, active, navigate }) => (
  <button onClick={() => navigate(href)}
    className={`px-3 py-4 text-sm transition-colors duration-200 relative ${active ? 'text-white' : 'text-[#B0BEC5] hover:text-[#4DD0E1]'}`}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}>
    {label}
    {active && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#00707C] rounded-full" />}
  </button>
);


// ─── Dropdown nav item ───
const DropdownNavItem = ({ label, sectionKey, active, items, openDropdown, onHoverEnter, onHoverLeave, onToggle, navigate }) => {
  const isOpen = openDropdown === sectionKey;
  return (
    <div className="relative" onMouseEnter={() => onHoverEnter(sectionKey)} onMouseLeave={onHoverLeave}>
      <button onClick={() => onToggle(sectionKey)}
        className={`flex items-center gap-1 px-3 py-4 text-sm transition-colors duration-200 relative ${active ? 'text-white' : 'text-[#B0BEC5] hover:text-[#4DD0E1]'}`}
        data-testid={`nav-${sectionKey}`}>
        {label} <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        {active && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#00707C] rounded-full" />}
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full mt-0 w-72 bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)] py-2 z-50 animate-in fade-in-0 slide-in-from-top-2 duration-150"
          onMouseEnter={() => onHoverEnter(sectionKey)} onMouseLeave={onHoverLeave}>
          {items.map(item => (
            <button key={item.href} onClick={() => { navigate(item.href); onToggle(null); }}
              className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-[#F5F5F5] transition-colors group border-l-[3px] border-transparent hover:border-[#0097A7]"
              data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <item.icon className="h-4 w-4 text-[#4E6974] group-hover:text-[#0097A7] mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#0A2533]">{item.label}</span>
                  {item.badge && <span className="text-[10px] text-[#4E6974] bg-[#F5F5F5] rounded px-1.5 py-0.5">{item.badge}</span>}
                </div>
                <p className="text-xs text-[#4E6974] mt-0.5">{item.desc}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── Mobile menu ───
const MobileMenu = ({ navigate, activeSection, companyName, clearProfile, onClose }) => {
  const [expanded, setExpanded] = useState(null);
  const { user: authUser, logout } = useAuth();
  const toggle = (key) => setExpanded(prev => prev === key ? null : key);

  return (
    <div className="fixed inset-0 top-14 z-40 bg-white overflow-y-auto lg:hidden" data-testid="mobile-menu">
      <div className="py-2">
        <MobileNavItem label="Dashboard" href="/dashboard" active={activeSection === 'dashboard'} navigate={navigate} />
        <MobileAccordion label="Intelligence" sectionKey="intelligence" active={activeSection === 'intelligence'} items={INTELLIGENCE_ITEMS} expanded={expanded} toggle={toggle} navigate={navigate} />
        <MobileAccordion label="My Supply Chain" sectionKey="supply-chain" active={activeSection === 'supply-chain'} items={SUPPLY_CHAIN_ITEMS} expanded={expanded} toggle={toggle} navigate={navigate} />
        <MobileAccordion label="Analytics" sectionKey="analytics" active={activeSection === 'analytics'} items={ANALYTICS_ITEMS} expanded={expanded} toggle={toggle} navigate={navigate} />
        <MobileNavItem label="Scenarios" href="/scenarios" active={activeSection === 'scenarios'} navigate={navigate} />
        <MobileNavItem label="Sourcing" href="/sourcing-alternatives" active={activeSection === 'sourcing'} navigate={navigate} />
        <MobileNavItem label="Reports" href="/reports" active={activeSection === 'reports'} navigate={navigate} />
        <div className="border-t border-[#E0E0E0] mt-2 pt-2 px-4">
          {authUser ? (
            <>
              <p className="text-sm font-semibold text-[#0A2533] mb-1">{authUser.name || companyName}</p>
              <p className="text-xs text-[#4E6974] mb-2">{authUser.email}</p>
              <button onClick={() => navigate('/settings')} className="w-full text-left py-2 text-sm text-[#4E6974]">Dashboard preferences</button>
              {authUser.is_platform_admin && (
                <button onClick={() => navigate('/admin')} className="w-full text-left py-2 text-sm text-[#0097A7] font-medium">Admin console</button>
              )}
              <button onClick={async () => { await logout(); clearProfile(); navigate('/signin'); }} className="w-full text-left py-2 text-sm text-[#4E6974]">Sign out</button>
            </>
          ) : (
            <button onClick={() => navigate('/signin')} className="w-full text-left py-2 text-sm text-[#0097A7] font-medium">Sign in</button>
          )}
        </div>
      </div>
    </div>
  );
};

const MobileNavItem = ({ label, href, active, navigate }) => (
  <button onClick={() => navigate(href)}
    className={`w-full text-left px-4 py-3 text-sm ${active ? 'text-[#0097A7] border-l-[3px] border-[#0097A7] bg-[#F5F5F5] font-medium' : 'text-[#0A2533] border-l-[3px] border-transparent'}`}>
    {label}
  </button>
);

const MobileAccordion = ({ label, sectionKey, active, items, expanded, toggle, navigate }) => (
  <div>
    <button onClick={() => toggle(sectionKey)}
      className={`w-full flex items-center justify-between px-4 py-3 text-sm ${active ? 'text-[#0097A7] border-l-[3px] border-[#0097A7] bg-[#F5F5F5] font-medium' : 'text-[#0A2533] border-l-[3px] border-transparent'}`}>
      {label} <ChevronDown className={`h-4 w-4 transition-transform ${expanded === sectionKey ? 'rotate-180' : ''}`} />
    </button>
    {expanded === sectionKey && (
      <div className="bg-[#FAFAFA]">
        {items.map(item => (
          <button key={item.href} onClick={() => navigate(item.href)}
            className="w-full flex items-center gap-3 px-8 py-2.5 text-sm text-[#4E6974] hover:text-[#0097A7] hover:bg-[#F5F5F5]">
            <item.icon className="h-4 w-4" /> {item.label}
          </button>
        ))}
      </div>
    )}
  </div>
);


// ─── Search modal ───
const SearchModal = ({ onClose, navigate }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const results = query.trim().length > 0
    ? SEARCH_INDEX.map(cat => ({
        ...cat,
        items: cat.items.filter(i => i.name.toLowerCase().includes(query.toLowerCase())),
      })).filter(cat => cat.items.length > 0)
    : SEARCH_INDEX.map(cat => ({ ...cat, items: cat.items.slice(0, 3) }));

  const handleSelect = (href) => { navigate(href); onClose(); };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="search-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E0E0E0]">
          <Search className="h-5 w-5 text-[#4E6974]" />
          <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search countries, suppliers, scenarios..."
            className="flex-1 text-sm text-[#0A2533] outline-none placeholder:text-[#B0BEC5]" data-testid="search-input" />
          <kbd className="hidden sm:inline text-[10px] text-[#B0BEC5] border border-[#E0E0E0] rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[400px] overflow-y-auto py-2">
          {results.length === 0 && <p className="text-center text-sm text-[#4E6974] py-8">No results found</p>}
          {results.map(cat => (
            <div key={cat.cat}>
              <p className="px-4 py-1.5 text-[10px] font-semibold text-[#4E6974] uppercase tracking-wider">{cat.cat}</p>
              {cat.items.map(item => (
                <button key={item.href} onClick={() => handleSelect(item.href)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#0A2533] hover:bg-[#F5F5F5] transition-colors" data-testid={`search-result-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <span className="flex-1 text-left">{item.name}</span>
                  <ChevronRight className="h-3 w-3 text-[#B0BEC5]" />
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NavBar;
